import React, { useCallback, useMemo } from 'react'

import { useFont, Tooltip } from '@octane/spark'
import CheckboxDropdown, {
    DropdownItem,
} from 'components/CheckboxDropdown/CheckboxDropdown'
import { ErrorMessage } from 'components/FormikFields/FormikDropdown/FormikDropdown.styled'
import GraphiteInformationIcon from 'components/GraphiteInformationIcon'
import styled from 'styled-components'

import {
    FieldConfigBase,
    FieldKit,
    FieldProps,
    FormValues,
} from '../../containers/FormBuilder'

const FieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    position: relative;
`

const FieldLabelContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const FieldLabel = styled.label`
    ${() => useFont('c14')}
    color: ${({ theme }) => theme.colors.monochrome.ravenSub};
    display: block;
    margin: 0;
    text-align: left;
`

const InfoIconContainer = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;

    svg {
        height: 12px;
        width: 12px;
        fill: ${({ theme }) => theme.colors.monochrome.graphite};
        margin-left: 5px;
    }
`

interface CheckboxDropdownFieldOption {
    id: string
    label: string
    value: number
}

export type CheckboxDropdownFieldOnChange = (
    selectedItems: DropdownItem[],
    fieldName: string,
    formValues: FormValues,
    fieldKit: FieldKit,
) => void

interface CheckboxDropdownFieldConfig extends FieldConfigBase {
    label: string
    options: CheckboxDropdownFieldOption[]
    isUnique?: boolean
    info?: JSX.Element
    onChange?: CheckboxDropdownFieldOnChange
    tooltip?: JSX.Element
    prefilled?: boolean
}

export default function CheckboxDropdownField(
    props: Readonly<FieldProps<CheckboxDropdownFieldConfig>>,
) {
    const { fieldName, fieldKit, fieldConfig } = props
    const touched = fieldKit.touched[fieldName]
    const error = fieldKit.errors[fieldName]
    const value = fieldKit.values[fieldName]

    // Convert stored value to DropdownItem array
    const dropdownItems: DropdownItem[] = useMemo(() => {
        const selectedIds = Array.isArray(value) ? value : []

        return fieldConfig.options.map((option) => ({
            id: option.id,
            label: option.label,
            value: option.value,
            disabled: false,
            checked: selectedIds.includes(option.id),
        }))
    }, [value, fieldConfig.options])

    const handleSetItems = useCallback(
        (items: DropdownItem[]) => {
            const selectedIds = items
                .filter((item) => item.checked)
                .map((item) => item.id)

            fieldKit.setFieldTouched(fieldName, true)

            if (fieldConfig.onChange) {
                fieldConfig.onChange(
                    items,
                    fieldName,
                    fieldKit.values,
                    fieldKit,
                )
            } else {
                const event = {
                    target: {
                        name: fieldName,
                        value: selectedIds,
                    },
                }

                fieldKit.handleChange(event)
            }

            fieldKit.setFieldValue(fieldName, selectedIds)
        },
        [fieldName, fieldKit, fieldConfig],
    )

    return (
        <FieldWrapper>
            <FieldLabelContainer>
                <FieldLabel htmlFor={fieldName}>{fieldConfig.label}</FieldLabel>
                {fieldConfig.info && (
                    <InfoIconContainer>
                        <Tooltip>
                            <Tooltip.Trigger>
                                <GraphiteInformationIcon />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {fieldConfig.info}
                            </Tooltip.Content>
                        </Tooltip>
                    </InfoIconContainer>
                )}
            </FieldLabelContainer>
            <CheckboxDropdown
                text=""
                items={dropdownItems}
                setItems={handleSetItems}
                isUnique={fieldConfig.isUnique}
                error={!!(touched && error)}
                prefilled={fieldConfig.prefilled}
            />

            {touched && error && <ErrorMessage>{error}</ErrorMessage>}
        </FieldWrapper>
    )
}

CheckboxDropdownField.defaultProps = {
    isUnique: false,
}
